// The design gallery (docs/design-system.md 6): each shared component and token
// as it really renders, so drift is visible. No data, no endpoint, no sync.
// It shows the code as it stands; known-bad cases are marked, not hidden.
// State in here is monochrome (icon and weight, D6) so it reads in black mode.
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { PageTitle, SectionTitle, Segmented, cx } from '@/components/ui'
import { useDevice } from '@/state/appearance'
import { BRIGHTNESS_LABEL, type Brightness } from '@/lib/themeLayers'
import { FIXTURE_HASH, FIXTURE_ID } from '@/design/fixtures'
import { REGISTRY, SECTIONS, type Example } from '@/design/registry'

/** D3: the phone's minimum tap target. */
export const MIN_TAP = 44

const SURFACES = ['ink', 'panel', 'raised', 'line', 'line-strong'] as const
const TEXTS = ['fg', 'fg-2', 'muted', 'faint', 'accent'] as const
const RADII = [
  ['card', '--radius-card'],
  ['card-inner', '--radius-card-inner'],
  ['control', '--radius-control'],
  ['sheet', '--radius-sheet'],
  ['composer', '--radius-composer'],
  ['bubble', '--radius-bubble'],
] as const

const Panel = ({ id, title, children }: { id: string; title: string; children: ReactNode }) => (
  <section id={id} className="scroll-mt-24 mb-8">
    <SectionTitle>{title}</SectionTitle>
    {children}
  </section>
)

/** Measures every button in its children against the 44px minimum and says so in words and an icon, never by hue. */
const TapCheck = ({ children }: { children: ReactNode }) => {
  const box = useRef<HTMLDivElement>(null)
  const [sizes, setSizes] = useState<number[]>([])
  useEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () =>
      setSizes([...el.querySelectorAll('button')].map((b) => Math.round(b.getBoundingClientRect().height)))
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const low = sizes.filter((h) => h > 0 && h < MIN_TAP)
  return (
    <div>
      <div ref={box}>{children}</div>
      {sizes.length > 0 && (
        <p
          className={cx('text-xs mt-1.5', low.length ? 'font-semibold text-fg' : 'text-faint')}
          data-tap={low.length ? 'low' : 'ok'}
        >
          {low.length ? '▲ ' : '✓ '}
          {sizes.join(', ')} px high
          {low.length ? `: below ${MIN_TAP} on a phone (D3)` : ''}
        </p>
      )}
    </div>
  )
}

const ExampleView = ({ ex }: { ex: Example }) => (
  <div className="mb-4">
    <div className="text-xs text-faint mb-1.5">{ex.label}</div>
    {ex.tap ? <TapCheck>{ex.render()}</TapCheck> : ex.render()}
  </div>
)

/** The resolved value of a CSS variable on <html>, re-read when the appearance changes. */
const VarReadout = ({ name }: { name: string }) => {
  const mode = useDevice((s) => s.layer.brightness)
  const out = useRef<HTMLElement>(null)
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (out.current)
        out.current.textContent =
          getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '(unset)'
    })
    return () => cancelAnimationFrame(id)
  }, [name, mode])
  return <code ref={out} className="break-all" />
}

const Foundations = () => (
  <>
    <Panel id="surfaces" title="Colour: surfaces and text">
      <div className="grid grid-cols-5 gap-1.5 mb-2">
        {SURFACES.map((s) => (
          <div
            key={s}
            className={cx(
              'h-14 rounded-[var(--radius-control)] border border-line text-[11px] p-1',
              `bg-${s}`,
            )}
          >
            {s}
          </div>
        ))}
      </div>
      <div className="card p-3 grid grid-cols-5 gap-1.5 text-sm">
        {TEXTS.map((t) => (
          <span key={t} className={`text-${t}`}>
            {t}
          </span>
        ))}
      </div>
    </Panel>
    <Panel id="type" title="Type: one family, four steps">
      <div className="card p-3 space-y-2">
        <div className="t-display">Display 34</div>
        <div className="t-title">Title 28</div>
        <div className="t-title2">Title 22</div>
        <div className="label">Label 13 semibold</div>
        <div className="text-[15px]">Body 15: Atkinson Hyperlegible Next</div>
        <div className="text-[11px] text-muted">Meta 11</div>
        <div className="text-xs text-faint">
          D5: <code>font-mono</code> is for ids, hashes, diffs and code only:
        </div>
        <div className="font-mono text-xs break-all">{FIXTURE_ID}</div>
        <div className="font-mono text-xs break-all">{FIXTURE_HASH}</div>
      </div>
    </Panel>
    <Panel id="radius" title="Radius pairs">
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        {RADII.map(([n, v]) => (
          <div
            key={n}
            className="h-16 border border-line-strong bg-panel p-1.5"
            style={{ borderRadius: `var(${v})` }}
          >
            {n}
            <div className="text-faint">
              <VarReadout name={v} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-faint mt-2">A card inside a card: inner = outer minus padding.</p>
      <div className="card p-1.5 mt-1">
        <div
          className="bg-raised h-10 border border-line"
          style={{ borderRadius: 'var(--radius-card-inner)' }}
        />
      </div>
    </Panel>
    <Panel id="elevation" title="Elevation (D1)">
      <div className="p-4 bg-ink">
        <div className="bg-panel border border-line rounded-[var(--radius-card)] p-3 text-sm shadow-[var(--shadow-pop)]">
          The one sanctioned shadow, for things that float. Zero in black.
          <div className="text-xs text-faint mt-1">
            <code>--shadow-pop</code>: <VarReadout name="--shadow-pop" />
          </div>
        </div>
      </div>
    </Panel>
  </>
)

export const DesignView = () => {
  const mode = useDevice((s) => s.layer.brightness)
  const set = useDevice((s) => s.set)
  // the gallery shows Horizon in the brightness chosen here, on this device
  const setMode = (b: Brightness) => set({ theme: { kind: 'horizon' }, brightness: b, glass: null })
  useEffect(() => {
    const id = window.location.hash.slice(1)
    if (id) requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView())
  }, [])
  const go = (id: string) => {
    window.history.replaceState(null, '', `#${id}`)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }
  const modes: Brightness[] = ['light', 'dark', 'black']
  return (
    <div className="max-w-3xl mx-auto pb-24">
      <PageTitle eyebrow="Dev">Design system</PageTitle>
      <p className="text-sm text-muted mb-3">
        Each shared component as it renders today, drift included. Plan and decisions: docs/design-system.md.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-faint">Appearance (this device)</span>
        <Segmented
          label="Appearance"
          value={mode && modes.includes(mode) ? mode : ('' as Brightness)}
          onChange={setMode}
          options={modes.map((m) => ({ value: m, label: BRIGHTNESS_LABEL[m] }))}
        />
      </div>
      <nav aria-label="Sections" className="flex gap-1.5 overflow-x-auto scrollbar-none pb-2 mb-4">
        {['surfaces', 'type', 'radius', 'elevation', ...REGISTRY.map((e) => e.id)].map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => go(id)}
            className="shrink-0 rounded-full border border-line bg-raised px-3 min-h-11 text-xs text-fg-2"
          >
            {id}
          </button>
        ))}
      </nav>
      <h2 className="t-title2 mb-3">Foundations</h2>
      <Foundations />
      {SECTIONS.map((section) => (
        <div key={section}>
          <h2 className="t-title2 mb-3">{section}</h2>
          {REGISTRY.filter((e) => e.section === section).map((e) => (
            <Panel key={e.id} id={e.id} title={e.name}>
              <p className="text-sm text-muted mb-1">{e.job}</p>
              {e.replaces && <p className="text-xs text-faint mb-2">Replaces: {e.replaces.join('; ')}</p>}
              <div className="mt-2">
                {e.examples.map((ex) => (
                  <ExampleView key={ex.label} ex={ex} />
                ))}
              </div>
            </Panel>
          ))}
        </div>
      ))}
    </div>
  )
}
